import { Injectable, inject } from '@angular/core';
import { StorageService } from '../storage.service';
import { NativeAnnotationItem } from '../indexdb/index.db';

@Injectable({
  providedIn: 'root',
})
export class AnnotationRepositoryService {
  private storage = inject(StorageService);

  protected _tableName = 'annotations';

  private getPageKey(docId: string, pageId: number): string {
    return `${this._tableName}:${docId}:${pageId}`;
  }

  private getIndexKey(docId: string): string {
    return `${this._tableName}:${docId}`;
  }

  private async getIndex(indexKey: string): Promise<number[]> {
    const exists = await this.storage.isKey(indexKey);
    if (!exists) return [];
    const indexJSON = await this.storage.getItem(indexKey);
    return JSON.parse(indexJSON) ?? [];
  }

  /**
   * This function sets initial dummy value in the table
   */
  async addDummy() {
    try {
      await this.setTable();
      await this.storage.setItem('0', '1');
    } catch (error) {
      console.error('AnnotationRepositoryService: ', error);
      return;
    }
  }

  // --------------------------
  //   Individual Page Methods
  // --------------------------

  async getPage(
    docId: string,
    pageId: number,
  ): Promise<NativeAnnotationItem | null> {
    try {
      await this.setTable();
      const key = this.getPageKey(docId, pageId);
      const exists = await this.storage.isKey(key);
      if (!exists) {
        return null;
      }
      const annotationJSON = await this.storage.getItem(key);
      return JSON.parse(annotationJSON);
    } catch (error) {
      console.error('AnnotationRepositoryService: ', error);
      return null;
    }
  }

  async createOrUpdatePage(
    docId: string,
    pageId: number,
    annotation: NativeAnnotationItem,
  ) {
    try {
      await this.setTable();
      const key = this.getPageKey(docId, pageId);
      await this.storage.setItem(key, JSON.stringify(annotation));

      // Update index
      const indexKey = this.getIndexKey(docId);
      const index = await this.getIndex(indexKey);
      if (!index.includes(pageId)) {
        index.push(pageId);
        await this.storage.setItem(indexKey, JSON.stringify(index));
      }
    } catch (error) {
      console.error('AnnotationRepositoryService: ', error);
    }
  }

  async deletePage(docId: string, pageId: number): Promise<void> {
    try {
      await this.setTable();
      const key = this.getPageKey(docId, pageId);
      const exists = await this.storage.isKey(key);
      if (!exists) {
        return;
      }
      await this.storage.removeItem(key);

      // Update index
      const indexKey = this.getIndexKey(docId);
      const index = await this.getIndex(indexKey);
      const updated = index.filter((id) => id !== pageId);
      await this.storage.setItem(indexKey, JSON.stringify(updated));
    } catch (error) {
      console.error('AnnotationRepositoryService: ', error);
    }
  }

  // ----------------------------
  //   Entire Document Methods
  // ----------------------------

  async createOrUpdate(
    docId: string,
    annotationMap: Map<string, NativeAnnotationItem>,
  ): Promise<void> {
    try {
      await this.setTable();
      const indexKey = this.getIndexKey(docId);
      const pageIds: number[] = [];

      // Create or update pages
      for (const [pageId, annotation] of annotationMap.entries()) {
        const page = Number(pageId);
        const pageKey = this.getPageKey(docId, page);
        await this.storage.setItem(pageKey, JSON.stringify(annotation));
        pageIds.push(page);
      }

      // Update index
      await this.storage.setItem(indexKey, JSON.stringify(pageIds));
    } catch (error) {
      console.error('AnnotationRepositoryService: ', error);
    }
  }

  async get(documentId: string) {
    try {
      await this.setTable();
      const indexKey = this.getIndexKey(documentId);
      const pageIds = await this.getIndex(indexKey);
      const annotations: Map<string, NativeAnnotationItem> = new Map();

      for (const pageId of pageIds) {
        const annotation = await this.getPage(documentId, pageId);
        if (annotation) {
          annotations.set(pageId.toString(), annotation);
        }
      }

      return annotations;
    } catch (error) {
      console.error('AnnotationRepositoryService: ', error);
      return null;
    }
  }

  async delete(documentId: string): Promise<void> {
    try {
      await this.setTable();
      const indexKey = this.getIndexKey(documentId);
      const pageIds = await this.getIndex(indexKey);

      for (const pageId of pageIds) {
        const key = this.getPageKey(documentId, pageId);
        const exists = await this.storage.isKey(key);
        if (exists) await this.storage.removeItem(key);
      }

      const exists = await this.storage.isKey(indexKey);
      if (exists) await this.storage.removeItem(indexKey);
    } catch (error) {
      console.error('AnnotationRepositoryService: ', error);
    }
  }

  /**
   * This function deletes all keys
   */
  async clear(): Promise<void> {
    try {
      await this.setTable();
      await this.storage.clear();
      await this.addDummy();
    } catch (error) {
      console.error('AnnotationRepositoryService: ', error);
    }
  }

  /**
   * This function sets storage table
   */
  async setTable() {
    this.storage.setTable(this._tableName);
  }

  /**
   * Runs the migration process to update annotation data to the new format.
   *
   * This method sets the storage table, finds all document keys in the old format,
   * and migrates their annotation data to the new format using `migrateFromOldFormat`.
   * If an error occurs during migration, it logs the error to the console.
   *
   * @returns {Promise<void>} A promise that resolves when migration is complete.
   */
  async runMigration(): Promise<void> {
    try {
      await this.setTable();
      // Migrate annotations from old format
      const allKeys = await this.storage.getAllKeys();
      const oldDocKeys = allKeys.filter((k) =>
        /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/.test(
          k,
        ),
      );
      if (oldDocKeys.length) await this.migrateFromOldFormat(oldDocKeys);
    } catch (error) {
      console.error('AnnotationRepositoryService: Migration failed', error);
    }
  }

  /**
   * Migrates annotation data from the old storage format to the new format.
   *
   * This method iterates over the provided document IDs, checks for the existence of old-format keys,
   * converts annotation objects to the new format by splitting them into per-page entries,
   * updates the index, and removes the old-format key from storage.
   *
   * @param {string[]} documentIds - Array of document IDs to migrate.
   * @returns {Promise<void>} A promise that resolves when migration is complete.
   */
  private async migrateFromOldFormat(documentIds: string[]): Promise<void> {
    for (const docId of documentIds) {
      const oldKey = `${docId}`;

      const exists = await this.storage.isKey(oldKey);
      if (!exists) continue;

      const annotationObjJSON = await this.storage.getItem(oldKey);
      const annotationObj = JSON.parse(annotationObjJSON);
      const annotation = new Map<string, string>(Object.entries(annotationObj));

      const pageIds: number[] = [];

      for (const pageStr of Array.from(annotation.keys())) {
        const pageId = parseInt(pageStr, 10);
        const pageAnnotation = annotation.get(pageStr);
        if (!pageAnnotation) continue;
        const newKey = this.getPageKey(docId, pageId);
        await this.storage.setItem(newKey, JSON.stringify(pageAnnotation));
        pageIds.push(pageId);
      }

      const indexKey = this.getIndexKey(docId);
      await this.storage.setItem(indexKey, JSON.stringify(pageIds));

      // Remove old key
      await this.storage.removeItem(oldKey);
    }
  }
}
